import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import { REQUEST_LOADING_STATES } from "../../helpers/constant";
import { IFeed, IFeedState, IGetFeedsParamsV2 } from "../../helpers/types";
import { feedService, reactionService } from "../../services";

const initialState: IFeedState = {
  otherUserFeeds: [],
  currentFeeds: null,
  currentUserFeeds: [],
  homeFeeds: [],
  bookmarkFeeds: [],
  list: [],
  item: null,
  loading: REQUEST_LOADING_STATES.IDLE,
  currentUserLoading: REQUEST_LOADING_STATES.IDLE,
  loadingHome: REQUEST_LOADING_STATES.IDLE,
  loadingBookmarkFeeds: REQUEST_LOADING_STATES.IDLE,
  error: null,
  errorBookmarkFeeds: null,
  totalPages: -1,
  total: -1,
  currentFeedsPage: 1,
  otherFeedsPage: 1,
  currentUserWithFeeds: null,
};

export const getMyFeeds = createAsyncThunk("feeds/getMyFeeds", async () => {
  return feedService.getMyFeeds();
});

export const getUserFeeds = createAsyncThunk("feeds/getUserFeeds", async (page: number) => {
  return feedService.getMyFeeds(page);
});

export const getHomePageFeeds = createAsyncThunk("feeds/getHomePageFeeds", async (page: number) => {
  return feedService.getHomePageFeeds(page);
});

export const getBookmarkFeeds = createAsyncThunk("feeds/getBookmarkFeeds", async () => {
  return reactionService.BookmarksFeeds();
});

export const loadMorePageFeeds = createAsyncThunk("feeds/loadMorePageFeeds", async (page: number) => {
  return feedService.getHomePageFeeds(page);
});

export const getFeedsByCreatorId = createAsyncThunk("feeds/getFeedsByCreatorId", async ({ creatorId, page }: IGetFeedsParamsV2) => {
  return feedService.getFeeds(creatorId, page);
});

export const getOtherUserFeedsById = createAsyncThunk("feeds/getOtherUserFeedsById", async ({ creatorId, page }: IGetFeedsParamsV2) => {
  const data = await feedService.getFeeds(creatorId, page);
  return { ...data, creatorId };
});

export const loadMoreFeedsByCreatorId = createAsyncThunk("feeds/loadMoreFeedsByCreatorId", async ({ creatorId, page }: IGetFeedsParamsV2) => {
  return feedService.getFeeds(creatorId, page);
});

export const getPublicFeedsByCreatorUsername = createAsyncThunk("feeds/getPublicFeedsByCreatorUsername", async (username: string) => {
  return feedService.getPublicFeeds(username);
});

export const deleteFeedById = createAsyncThunk("feeds/deleteFeedById", async (feedId: string) => {
  return feedService.deleteFeed(feedId);
});

export const removeOldState = createAsyncThunk("feeds/removeOldState", async () => {
  return [];
});

const feedsSlice = createSlice({
  name: "feeds",
  initialState,
  reducers: {
    updateFile: (state, action) => {
      const fileToUpdate = action.payload;
      let found = false;
      state.currentUserFeeds = state.currentUserFeeds.map((feed) => {
        if (!found) {
          feed.files = feed.files?.map((file) => {
            if (file._id === fileToUpdate.fileId && !found) {
              file = {
                ...file,
                url: fileToUpdate.absolutePath,
                thumbnails: fileToUpdate.thumbnails,
                status: fileToUpdate.status,
              };
              found = true;
            }
            return file;
          });
        }
        return feed;
      });
    },
    updateBookmarkState: (state, action) => {
      const { feedId, isBookmark, creatorId, isMyProfile } = action.payload;
      state.homeFeeds = state.homeFeeds.map((post) => {
        if (post?._id === feedId) {
          post.isBookMarked = isBookmark;
        }
        return post;
      });
      if (isMyProfile) {
        const bookmarkedFeed = state.currentFeeds?.feeds.find((feed) => feed._id === feedId);
        if (bookmarkedFeed) {
          bookmarkedFeed.isBookMarked = isBookmark;
        }
      } else {
        const otherUserFeeds = state.otherUserFeeds.find((creator) => creator.id === creatorId);
        if (otherUserFeeds) {
          const bookmarkedFeed = otherUserFeeds.feeds.find((feed) => feed._id === feedId);
          if (bookmarkedFeed) {
            bookmarkedFeed.isBookMarked = isBookmark;
          }
        }
      }
    },
    updateLikeState: (state, action) => {
      const { feedId, isLiked, creatorId, isMyProfile } = action.payload;
      state.homeFeeds = state.homeFeeds.map((post) => {
        if (post?._id === feedId) {
          post.isLiked = isLiked;
          if (post.totalLike !== undefined) {
            post.totalLike = isLiked ? post.totalLike + 1 : post?.totalLike - 1;
          }
        }
        return post;
      });
      if (isMyProfile) {
        const likedFeed = state.currentFeeds?.feeds.find((feed) => feed._id === feedId);
        if (likedFeed) {
          likedFeed.isLiked = isLiked;
          if (likedFeed.totalLike !== undefined) {
            likedFeed.totalLike = isLiked ? likedFeed.totalLike + 1 : likedFeed?.totalLike - 1;
          }
        }
      } else {
        const otherUserFeeds = state.otherUserFeeds.find((creator) => creator.id === creatorId);
        if (otherUserFeeds) {
          const likedFeed = otherUserFeeds.feeds.find((feed) => feed._id === feedId);
          if (likedFeed) {
            likedFeed.isLiked = isLiked;
            if (likedFeed.totalLike !== undefined) {
              likedFeed.totalLike = isLiked ? likedFeed.totalLike + 1 : likedFeed?.totalLike - 1;
            }
          }
        }
      }
    },
    resetCurrentFeeds: (state) => {
      state.currentFeeds = null;
      state.currentFeedsPage = 1;
    },
    resetOtherCurrentFeeds: (state, action) => {
      if (action.payload !== state.currentUserWithFeeds) {
        state.otherUserFeeds = [];
        state.otherFeedsPage = 1;
      }
    },
    resetFeeds: (state) => {
      state.otherUserFeeds = [];
      state.homeFeeds = [];
      state.currentFeeds = null;
      state.currentFeedsPage = 1;
      state.otherFeedsPage = 1;
      state.total = -1;
      state.totalPages = -1;
    },
    filterFeedsByCreatorId: (state, action) => {
      const creatorId = action.payload;
      state.homeFeeds = state.homeFeeds.filter((feed) => feed.sourceId !== creatorId);
    },
    changeCurrentUserWithFeeds: (state, action) => {
      state.otherUserFeeds = [];
      state.otherFeedsPage = 1;
      state.currentUserWithFeeds = action.payload;
    },
    addFeed: (state, action) => {
      state.error = null;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const currentFeeds = state.currentFeeds;
      if (!currentFeeds) {
        state.currentFeeds = {
          id: "",
          feeds: [action.payload],
          ttl: 5,
          lft: new Date().getTime(),
          total: 1,
          hasMore: false,
          offset: 1,
        };
        return;
      }
      const newFeeds = uniqBy([action.payload, ...currentFeeds.feeds], "_id");
      state.currentFeeds = {
        ...state.currentFeeds,
        id: "",
        ttl: 5,
        lft: new Date().getTime(),
        total: currentFeeds.total + 1,
        feeds: newFeeds,
        hasMore: currentFeeds.hasMore,
        offset: currentFeeds.offset + 1,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOtherUserFeedsById.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(getOtherUserFeedsById.fulfilled, (state, action: any) => {
      state.error = null;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const feeds: IFeed[] = action.payload.data;
      const total = action?.payload?.total;

      const otherUserFeeds = state.otherUserFeeds;
      const user = otherUserFeeds.findIndex((user) => user.id === action?.payload?.creatorId);
      if (user !== -1) {
        const newFeeds = uniqBy([...otherUserFeeds[user].feeds, ...feeds], "_id");
        otherUserFeeds[user] = {
          ...otherUserFeeds[user],
          feeds: newFeeds,
          ttl: 5,
          lft: new Date().getTime(),
          hasMore: newFeeds.length < total || newFeeds.length !== total,
          offset: newFeeds.length,
          total,
        };
        state.otherUserFeeds = otherUserFeeds;
      } else {
        state.otherUserFeeds = uniqBy(
          [
            ...otherUserFeeds,
            {
              id: action?.payload?.creatorId,
              feeds: feeds,
              ttl: 5,
              lft: new Date().getTime(),
              hasMore: feeds.length < total || feeds.length !== total,
              offset: feeds.length,
              total,
            },
          ],
          "id"
        );
      }
      state.otherFeedsPage = action.meta.arg.page + 1;
    });
    builder.addCase(getOtherUserFeedsById.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getUserFeeds.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addCase(getUserFeeds.fulfilled, (state, action: any) => {
      state.error = null;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const feeds: IFeed[] = action.payload.data;
      const total = action?.payload?.total;
      const currentFeeds = state.currentFeeds;
      if (currentFeeds) {
        if (action.meta.arg !== 1) {
          const newFeeds = uniqBy([...currentFeeds.feeds, ...action.payload.data], "_id");
          state.currentFeeds = {
            ...state.currentFeeds,
            id: "",
            ttl: 5,
            lft: new Date().getTime(),
            total,
            feeds: newFeeds,
            offset: currentFeeds.feeds.length,
            hasMore: currentFeeds.feeds.length < action.payload.total,
          };
        }
      } else {
        state.currentFeeds = {
          id: "",
          feeds: feeds,
          ttl: 5,
          lft: new Date().getTime(),
          hasMore: feeds.length < total || feeds.length !== total,
          offset: feeds.length,
          total,
        };
      }
      state.currentFeedsPage = action.meta.arg + 1;
    });
    builder.addCase(getUserFeeds.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getHomePageFeeds.pending, (state) => {
      state.loadingHome = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(getHomePageFeeds.fulfilled, (state, action) => {
      state.homeFeeds = action.payload.data || [];
      state.loadingHome = REQUEST_LOADING_STATES.SUCCEEDED;
      state.totalPages = action.payload.totalPages;
    });
    builder.addCase(getHomePageFeeds.rejected, (state, action) => {
      state.loadingHome = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(getBookmarkFeeds.pending, (state) => {
      state.loadingBookmarkFeeds = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(getBookmarkFeeds.fulfilled, (state, action) => {
      state.bookmarkFeeds = action.payload || [];
      state.loadingBookmarkFeeds = REQUEST_LOADING_STATES.SUCCEEDED;
    });
    builder.addCase(getBookmarkFeeds.rejected, (state, action) => {
      state.loadingBookmarkFeeds = REQUEST_LOADING_STATES.FAILED;
      state.errorBookmarkFeeds = action.error.message;
    });
    builder.addCase(getPublicFeedsByCreatorUsername.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.list = [];
    });
    builder.addCase(getPublicFeedsByCreatorUsername.fulfilled, (state, action) => {
      state.list = action.payload || [];
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
    });
    builder.addCase(getPublicFeedsByCreatorUsername.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(loadMorePageFeeds.pending, (state) => {
      //state.loading = "pending";
    });
    builder.addCase(loadMorePageFeeds.fulfilled, (state, action) => {
      state.homeFeeds = [...state.homeFeeds, ...action.payload.data];
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.totalPages = action.payload.totalPages;
    });
    builder.addCase(loadMorePageFeeds.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(deleteFeedById.fulfilled, (state, action) => {
      const id = action.payload._id;
      if (state.currentFeeds && state.currentFeeds.feeds) {
        const newList = state.currentFeeds.feeds.filter((item) => item._id !== id);
        state.currentFeeds = {
          ...state.currentFeeds,
          feeds: newList,
        };
      }
      state.currentUserLoading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });
    builder.addCase(loadMoreFeedsByCreatorId.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(loadMoreFeedsByCreatorId.fulfilled, (state, action: any) => {
      state.list = [...state.list, ...action.payload.data];
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.total = action.payload.total;
    });
    builder.addCase(loadMoreFeedsByCreatorId.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(removeOldState.fulfilled, (state, action: any) => {
      state.list = [];
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.total = null;
    });
    builder.addMatcher(isAnyOf(getMyFeeds.fulfilled), (state, action: any) => {
      state.currentUserFeeds = action.payload.data;
      state.currentUserLoading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(getMyFeeds.pending), (state, action: any) => {
      state.currentUserLoading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(getMyFeeds.rejected), (state, action: any) => {
      state.currentUserLoading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addMatcher(isAnyOf(deleteFeedById.pending), (state, action) => {
      state.currentUserLoading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });

    builder.addMatcher(isAnyOf(deleteFeedById.rejected), (state, action) => {
      state.currentUserLoading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addMatcher(isAnyOf(getFeedsByCreatorId.fulfilled), (state, action: any) => {
      state.list = action.payload.data;
      state.error = null;
      state.total = action?.payload?.total;
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
    });
    builder.addMatcher(isAnyOf(getFeedsByCreatorId.pending), (state, action) => {
      state.list = [];
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
      state.total = -1;
    });
    builder.addMatcher(isAnyOf(getFeedsByCreatorId.rejected), (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  updateFile,
  updateBookmarkState,
  updateLikeState,
  resetCurrentFeeds,
  resetFeeds,
  filterFeedsByCreatorId,
  resetOtherCurrentFeeds,
  changeCurrentUserWithFeeds,
  addFeed,
} = feedsSlice.actions;

export default feedsSlice.reducer;
